// Shared "upload a report" control for the patient portal.
//
// Used from two places with different styling systems — the Tailwind sidebar
// (`PatientRightPanel`) and the plain-CSS portal home page — so the trigger is
// a render prop and all of this component's own chrome uses inline styles,
// which work in both. Nothing here depends on `#clinic-root` being an ancestor.
//
// Files go to the backend one at a time rather than in a single multi-file
// request: each upload is dual-filed and cache-rowed independently, so one bad
// file (wrong type, too large) must not take the rest of the batch down with it.
import React, { useCallback, useRef, useState } from "react";
import { uploadPatientReport } from "./patientPortalApi";

const ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.gif,.bmp,.webp,.heic,.heif,.txt,.csv,.doc,.docx,.rtf";

const TONES = {
  pending: { color: "#64748b", icon: "…" },
  done: { color: "#15803d", icon: "✓" },
  error: { color: "#dc2626", icon: "✕" },
};

// "went to your Aug 4 visit and your chart" — tells the patient where the file
// actually landed, since dual-filing is otherwise invisible to them.
function destinationLabel(result) {
  const places = [];
  if (result?.filed_to_visit) {
    const date = result.visit_date;
    // The backend files against the soonest UPCOMING visit, or the most recent
    // PAST one when nothing is scheduled — so never say "next visit" without
    // knowing which. With a date in hand "your Aug 4 visit" reads correctly
    // either way; the fallback has to stay neutral.
    let when = result?.visit_is_upcoming ? "your next visit" : "your latest visit";
    if (date) {
      const parsed = new Date(`${date}T00:00:00`);
      if (!isNaN(parsed.getTime())) {
        when = `your ${parsed.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        })} visit`;
      }
    }
    places.push(when);
  }
  if (result?.filed_to_chart) places.push("your chart");
  if (!places.length) return "Sent to your care team";
  return `Sent to ${places.join(" and ")}`;
}

export default function PatientReportUpload({
  onUploaded,
  renderTrigger,
  buttonClassName,
  buttonLabel = "Upload a report",
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      setBusy(true);
      setItems(files.map((f) => ({ name: f.name, status: "pending", note: "Uploading…" })));

      const results = [];
      for (let i = 0; i < files.length; i += 1) {
        try {
          const result = await uploadPatientReport(files[i]);
          results.push(result);
          setItems((prev) =>
            prev.map((it, idx) =>
              idx === i
                ? { ...it, status: "done", note: destinationLabel(result) }
                : it
            )
          );
        } catch (err) {
          setItems((prev) =>
            prev.map((it, idx) =>
              idx === i
                ? { ...it, status: "error", note: err.message || "Upload failed" }
                : it
            )
          );
        }
      }

      setBusy(false);
      if (results.length && onUploaded) onUploaded(results);
      // Reset the input so re-picking the SAME file still fires onChange.
      if (inputRef.current) inputRef.current.value = "";
    },
    [onUploaded]
  );

  const open = () => {
    if (!disabled && !busy && inputRef.current) inputRef.current.click();
  };

  const trigger = renderTrigger ? (
    renderTrigger({ onClick: open, disabled: disabled || busy, busy })
  ) : (
    <button
      type="button"
      onClick={open}
      disabled={disabled || busy}
      className={buttonClassName}
    >
      {busy ? "Uploading…" : buttonLabel}
    </button>
  );

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        onChange={(e) => handleFiles(e.target.files)}
        style={{ display: "none" }}
        data-testid="patient-report-file-input"
      />
      {trigger}
      {items.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: "10px 0 0",
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {items.map((item, idx) => {
            const tone = TONES[item.status] || TONES.pending;
            return (
              <li
                key={`${item.name}-${idx}`}
                style={{ fontSize: 12, lineHeight: 1.4, color: tone.color }}
              >
                <span aria-hidden style={{ marginRight: 6 }}>
                  {tone.icon}
                </span>
                <span style={{ fontWeight: 600 }}>{item.name}</span>
                {item.note ? (
                  <span style={{ opacity: 0.9 }}> — {item.note}</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
